import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { parseErrorMessage } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { AlertCircle, CheckCircle2, Loader2, RefreshCw } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { BillingSettlementErrorRow } from "./types";
import { formatStage } from "./helpers";

export function BillingErrorsPanel() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [unresolvedOnly, setUnresolvedOnly] = useState(true);
  const [resolveDialog, setResolveDialog] = useState<{ open: boolean; row: BillingSettlementErrorRow | null }>({ open: false, row: null });
  const [resolveNote, setResolveNote] = useState("");

  const errorsQuery = useQuery<{ errors: BillingSettlementErrorRow[]; unresolvedCount: number }>({
    queryKey: ["/api/admin/billing/settlement-errors", unresolvedOnly],
    queryFn: async () => {
      const url = `/api/admin/billing/settlement-errors?unresolvedOnly=${unresolvedOnly ? "true" : "false"}&limit=200`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load billing settlement errors");
      return res.json();
    },
    refetchInterval: 60_000,
  });

  const settleMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      const res = await fetch("/api/admin/billing/settle-seam-lock-charges", {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Settlement run failed");
      return res.json();
    },
    onSuccess: (data: { usersEvaluated?: number; usersProcessed?: number; usersCharged: number; failureCount: number }) => {
      const evaluated = data.usersEvaluated ?? data.usersProcessed ?? data.usersCharged;
      toast({
        title: "Settlement run complete",
        description: `${evaluated} user(s) evaluated, ${data.usersCharged} charged, ${data.failureCount ?? 0} failure(s).`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/billing/settlement-errors"] });
    },
    onError: (err: Error) => {
      toast({ title: "Settlement run failed", description: parseErrorMessage(err, "Unknown error"), variant: "destructive" });
    },
  });

  const resolveMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (input: { id: number; note: string }) => {
      const res = await fetch(`/api/admin/billing/settlement-errors/${input.id}/resolve`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ note: input.note }),
      });
      if (!res.ok) throw new Error("Failed to resolve error");
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Error marked resolved" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/billing/settlement-errors"] });
      setResolveDialog({ open: false, row: null });
      setResolveNote("");
    },
    onError: (err: Error) => {
      toast({ title: "Resolve failed", description: parseErrorMessage(err, "Unknown error"), variant: "destructive" });
    },
  });

  const retryMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (input: { id: number }) => {
      const res = await fetch(`/api/admin/billing/settlement-errors/${input.id}/retry`, {
        method: "POST",
        credentials: "include",
      });
      const body = (await res.json().catch(() => ({}))) as {
        success?: boolean;
        error?: string | null;
        message?: string;
      };
      if (!res.ok) {
        throw new Error(body.message ?? "Retry failed");
      }
      return {
        success: body.success ?? false,
        error: body.error ?? null,
      };
    },
    onSuccess: (data) => {
      if (data.success) {
        toast({
          title: "Settlement re-ran successfully",
          description: "The error has been auto-resolved.",
        });
      } else {
        toast({
          title: "Retry still failed",
          description: data.error ?? "A new error row was logged.",
          variant: "destructive",
        });
      }
      queryClient.invalidateQueries({ queryKey: ["/api/admin/billing/settlement-errors"] });
    },
    onError: (err: Error) => {
      toast({ title: "Retry request failed", description: parseErrorMessage(err, "Unknown error"), variant: "destructive" });
    },
  });

  function formatKind(kind: string): string {
    if (kind === "seam_lock") return "Seam Lock Charge";
    if (kind === "proration_credit") return "Proration Credit";
    if (kind === "proration_charge") return "Proration Charge";
    return kind;
  }

  const rows = errorsQuery.data?.errors ?? [];
  const unresolvedCount = errorsQuery.data?.unresolvedCount ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold" data-testid="text-billing-errors-title">
            Billing Settlement Errors
          </h3>
          <p className="text-sm text-muted-foreground">
            Per-user failures from the nightly Seam lock charge cron (1:30 AM) and admin-triggered settlement runs.
            Hosts may be billed incorrectly until each error is investigated and acknowledged.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => settleMutation.mutate()}
            disabled={settleMutation.isPending}
            data-testid="button-rerun-seam-lock-settlement"
          >
            {settleMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Re-run Settlement
          </Button>
        </div>
      </div>

      <Card className="p-6" data-testid="card-billing-errors-summary">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            {unresolvedCount === 0 ? (
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            ) : (
              <AlertCircle className="h-5 w-5 text-red-600" />
            )}
            <span className="font-semibold" data-testid="text-billing-errors-unresolved-count">
              {unresolvedCount === 0
                ? "No unresolved billing settlement errors"
                : `${unresolvedCount} unresolved billing settlement error${unresolvedCount === 1 ? "" : "s"}`}
            </span>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={unresolvedOnly}
              onCheckedChange={(v) => setUnresolvedOnly(v === true)}
              data-testid="checkbox-billing-errors-unresolved-only"
            />
            <span>Show unresolved only</span>
          </label>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden" data-testid="card-billing-errors-table">
        {errorsQuery.isLoading ? (
          <div className="p-6"><Skeleton className="h-32 w-full" /></div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground" data-testid="text-billing-errors-empty">
            {unresolvedOnly ? "No unresolved errors. The settlement cron is running cleanly." : "No errors recorded yet."}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Kind / Stage</TableHead>
                <TableHead>Period</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Error</TableHead>
                <TableHead>Triggered By</TableHead>
                <TableHead>Attempts</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} data-testid={`row-billing-error-${row.id}`}>
                  <TableCell className="text-xs whitespace-nowrap">
                    {new Date(row.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-xs">
                    <div className="font-mono">#{row.userId}</div>
                    {row.userEmail && (
                      <div className="text-muted-foreground" data-testid={`text-billing-error-email-${row.id}`}>
                        {row.userEmail}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">
                    <div className="font-medium">{formatKind(row.kind)}</div>
                    <div className="text-muted-foreground">{formatStage(row.stage)}</div>
                    {row.properties && row.properties.length > 0 && (
                      <div
                        className="mt-1 text-muted-foreground"
                        data-testid={`text-billing-error-properties-${row.id}`}
                      >
                        {row.properties.map((p) => p.name).join(", ")}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs font-mono">{row.snapshotMonth ?? "—"}</TableCell>
                  <TableCell className="text-right text-xs font-mono">
                    {row.amountCents == null ? "—" : `$${(row.amountCents / 100).toFixed(2)}`}
                  </TableCell>
                  <TableCell className="text-xs max-w-md">
                    <div className="line-clamp-3 break-words" data-testid={`text-billing-error-message-${row.id}`}>
                      {row.errorMessage}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{row.triggeredBy}</TableCell>
                  <TableCell className="text-xs">
                    {row.attemptCount > 1 ? (
                      <Badge
                        variant="outline"
                        className="border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-200"
                        data-testid={`badge-billing-error-attempts-${row.id}`}
                      >
                        Retried · {row.attemptCount}×
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground" data-testid={`text-billing-error-attempts-${row.id}`}>
                        1×
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {row.resolvedAt ? (
                      <Badge className="bg-green-100 text-green-800 border-green-300" data-testid={`badge-billing-error-resolved-${row.id}`}>
                        Resolved
                      </Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-800 border-red-300" data-testid={`badge-billing-error-open-${row.id}`}>
                        Open
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {!row.resolvedAt && (
                      <div className="flex flex-col gap-1">
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => retryMutation.mutate({ id: row.id })}
                          disabled={retryMutation.isPending}
                          data-testid={`button-retry-billing-error-${row.id}`}
                        >
                          {retryMutation.isPending && retryMutation.variables?.id === row.id ? (
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          ) : (
                            <RefreshCw className="h-3 w-3 mr-1" />
                          )}
                          Retry now
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setResolveDialog({ open: true, row });
                            setResolveNote("");
                          }}
                          data-testid={`button-resolve-billing-error-${row.id}`}
                        >
                          Mark Resolved
                        </Button>
                      </div>
                    )}
                    {row.resolvedAt && row.resolutionNote && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-xs text-muted-foreground cursor-help">Note</span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs whitespace-pre-wrap">
                          {row.resolutionNote}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog
        open={resolveDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setResolveDialog({ open: false, row: null });
            setResolveNote("");
          }
        }}
      >
        <DialogContent data-testid="dialog-resolve-billing-error">
          <DialogHeader>
            <DialogTitle>Mark Billing Error Resolved</DialogTitle>
            <DialogDescription>
              {resolveDialog.row && (
                <span>
                  Error #{resolveDialog.row.id} for user #{resolveDialog.row.userId}
                  {resolveDialog.row.userEmail ? ` (${resolveDialog.row.userEmail})` : ""}.
                  Add a note describing how the underlying cause was fixed.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={resolveNote}
            onChange={(e) => setResolveNote(e.target.value)}
            placeholder="Resolution note (optional)…"
            rows={4}
            data-testid="input-resolve-billing-error-note"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setResolveDialog({ open: false, row: null });
                setResolveNote("");
              }}
              data-testid="button-cancel-resolve-billing-error"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (resolveDialog.row) {
                  resolveMutation.mutate({ id: resolveDialog.row.id, note: resolveNote });
                }
              }}
              disabled={resolveMutation.isPending}
              data-testid="button-confirm-resolve-billing-error"
            >
              {resolveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Mark Resolved
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
